import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ChevronRight } from "lucide-react";
import { SITE } from "@/lib/site";

const SERVICE_LINKS = [
  { to: "/commercial-signs-richmond-va", label: "Commercial Signs" },
  { to: "/storefront-signs-richmond-va", label: "Storefront Signs" },
  { to: "/monument-signs-richmond-va", label: "Monument Signs" },
  { to: "/sign-installation-richmond-va", label: "Sign Installation" },
  { to: "/new-business-signage-richmond-va", label: "New-Business Signage" },
];

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/resources", label: "Answers" },
  { to: "/about", label: "About Todd" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false); // mobile menu
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false); // desktop dropdown
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false); // mobile submenu
  const servicesRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [pathname]);

  // Close desktop services dropdown on outside click / Escape
  useEffect(() => {
    if (!servicesOpen) return;
    const onClick = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setServicesOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [servicesOpen]);

  const isOnServicePage = SERVICE_LINKS.some((s) => pathname === s.to);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur border-b border-[#E2E8F0]" : "bg-white border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <div className="leading-none">
              <div className="font-heading text-[#1A1A1A] text-xl tracking-tight" style={{ fontWeight: 900, letterSpacing: "-0.01em" }}>
                RVA<span className="text-[#F97316] mx-1">·</span>SIGNS
              </div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#5F6B73] mt-1.5 font-semibold">A Richmond Signage Resource</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="nav-links">
            <NavLink
              to="/"
              end
              data-testid="nav-link-home"
              className={({ isActive }) =>
                `text-sm font-semibold tracking-tight link-underline whitespace-nowrap ${
                  isActive ? "text-[#1A1A1A]" : "text-[#2B2F33]/75 hover:text-[#1A1A1A]"
                }`
              }
            >
              Home
            </NavLink>

            <NavLink
              to="/resources"
              data-testid="nav-link-answers"
              className={({ isActive }) =>
                `text-sm font-semibold tracking-tight link-underline whitespace-nowrap ${
                  isActive ? "text-[#1A1A1A]" : "text-[#2B2F33]/75 hover:text-[#1A1A1A]"
                }`
              }
            >
              Answers
            </NavLink>

            <NavLink
              to="/about"
              data-testid="nav-link-about-todd"
              className={({ isActive }) =>
                `text-sm font-semibold tracking-tight link-underline whitespace-nowrap ${
                  isActive ? "text-[#1A1A1A]" : "text-[#2B2F33]/75 hover:text-[#1A1A1A]"
                }`
              }
            >
              About Todd
            </NavLink>

            <NavLink
              to="/projects"
              data-testid="nav-link-projects"
              className={({ isActive }) =>
                `text-sm font-semibold tracking-tight link-underline whitespace-nowrap ${
                  isActive ? "text-[#1A1A1A]" : "text-[#2B2F33]/75 hover:text-[#1A1A1A]"
                }`
              }
            >
              Projects
            </NavLink>

            {/* SERVICES DROPDOWN */}
            <div ref={servicesRef} className="relative">
              <button
                type="button"
                data-testid="nav-link-services"
                aria-haspopup="menu"
                aria-expanded={servicesOpen}
                onClick={() => setServicesOpen((v) => !v)}
                className={`flex items-center gap-1 text-sm font-semibold tracking-tight whitespace-nowrap link-underline ${
                  isOnServicePage ? "text-[#1A1A1A]" : "text-[#2B2F33]/75 hover:text-[#1A1A1A]"
                }`}
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div
                  role="menu"
                  data-testid="nav-services-dropdown"
                  className="absolute left-0 mt-4 min-w-[280px] bg-white border border-[#E2E8F0] shadow-lg py-2"
                >
                  {SERVICE_LINKS.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      role="menuitem"
                      data-testid={`nav-services-item-${s.to.replace(/[^a-z]/g, "-")}`}
                      className="block px-5 py-3 text-sm font-semibold text-[#2B2F33] hover:bg-[#F8F8F5] hover:text-[#F97316] transition-colors"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/contact"
              data-testid="nav-link-contact"
              className={({ isActive }) =>
                `text-sm font-semibold tracking-tight link-underline whitespace-nowrap ${
                  isActive ? "text-[#1A1A1A]" : "text-[#2B2F33]/75 hover:text-[#1A1A1A]"
                }`
              }
            >
              Contact
            </NavLink>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={SITE.phoneHref}
              data-testid="nav-phone"
              className="flex items-center gap-2 text-sm font-bold text-[#1A1A1A] hover:text-[#F97316] transition-colors"
            >
              <Phone className="w-4 h-4" />
              {SITE.phone}
            </a>
            <Link
              to="/contact"
              data-testid="nav-cta-talk-to-todd"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase tracking-wider px-5 py-3 transition-colors"
            >
              Talk to Todd
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-[#1A1A1A]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E2E8F0] bg-white" data-testid="nav-mobile-panel">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-sm font-semibold border-b border-[#E2E8F0]/60 ${
                    isActive ? "text-[#1A1A1A]" : "text-[#2B2F33]/75"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}

            {/* Mobile services submenu */}
            <button
              type="button"
              data-testid="nav-mobile-services-toggle"
              onClick={() => setMobileServicesOpen((v) => !v)}
              aria-expanded={mobileServicesOpen}
              className={`flex items-center justify-between py-3 text-sm font-semibold border-b border-[#E2E8F0]/60 ${
                isOnServicePage ? "text-[#1A1A1A]" : "text-[#2B2F33]/75"
              }`}
            >
              <span>Services</span>
              <ChevronRight className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-90" : ""}`} />
            </button>
            {mobileServicesOpen && (
              <div data-testid="nav-mobile-services-list" className="pl-4 border-l-2 border-[#F97316]/30 my-2">
                {SERVICE_LINKS.map((s) => (
                  <NavLink
                    key={s.to}
                    to={s.to}
                    data-testid={`nav-mobile-services-item-${s.to.replace(/[^a-z]/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-2.5 text-sm font-semibold ${
                        isActive ? "text-[#F97316]" : "text-[#2B2F33]/85 hover:text-[#F97316]"
                      }`
                    }
                  >
                    {s.label}
                  </NavLink>
                ))}
              </div>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2">
              <a
                href={SITE.phoneHref}
                className="bg-[#1A1A1A] hover:bg-[#0A0A0A] text-white py-3 text-center text-sm font-bold uppercase tracking-wider transition-colors"
                data-testid="nav-mobile-call"
              >
                Call Todd
              </a>
              <Link
                to="/contact"
                data-testid="nav-mobile-cta"
                className="bg-[#F97316] text-white py-3 text-center text-sm font-bold uppercase tracking-wider"
              >
                Free Review
              </Link>
            </div>
            <div className="mt-3 flex flex-col gap-1.5 text-xs text-[#5F6B73] font-semibold">
              <a href={SITE.phoneHref} className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {SITE.phone}</a>
              <a href={SITE.emailHref} className="break-all">{SITE.email}</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
